import { ChangeEvent, ClipboardEvent, KeyboardEvent, useEffect, useRef, useState } from 'react'
import { Link, Navigate, useLocation } from 'react-router-dom'

import * as api from 'api/api'
import { useAuth } from 'auth/AuthContext'
import { Button } from 'components/ui'
import { Icon } from 'components/Icon'
import { errorMessage } from 'lib/hooks'

import { AuthShell } from './AuthShell'
import { postAuthPath } from './postAuthRedirect'

const DIGITS = 6
const RESEND_SECONDS = 45

const COPY = {
  ar: {
    title: 'تأكيد هويتك',
    subtitle: (contact?: string) =>
      contact ? `أدخل الرمز المكوّن من 6 أرقام المُرسل إلى ${contact}.` : 'أدخل الرمز المكوّن من 6 أرقام المُرسل إليك.',
    label: 'رمز التحقق',
    noCode: 'لم يصلك الرمز؟',
    resendIn: (s: number) => `إعادة الإرسال خلال 0:${String(s).padStart(2, '0')}`,
    resend: 'إعادة إرسال الرمز',
    submit: 'تأكيد',
    back: 'العودة لتسجيل الدخول',
  },
  en: {
    title: "Verify it's you",
    subtitle: (contact?: string) =>
      contact ? `Enter the 6-digit code we sent to ${contact}.` : 'Enter the 6-digit code we sent you.',
    label: 'Verification code',
    noCode: "Didn't get a code?",
    resendIn: (s: number) => `Resend in 0:${String(s).padStart(2, '0')}`,
    resend: 'Resend code',
    submit: 'Verify',
    back: 'Back to sign in',
  },
} as const

export const OtpVerificationPage = () => {
  const { user, setSession } = useAuth()
  const location = useLocation()
  const contact = (location.state as { contact?: string } | null)?.contact

  const [digits, setDigits] = useState<string[]>(() => Array(DIGITS).fill(''))
  const [seconds, setSeconds] = useState(RESEND_SECONDS)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    if (seconds <= 0) return undefined
    const id = window.setInterval(() => setSeconds((value) => Math.max(0, value - 1)), 1000)
    return () => window.clearInterval(id)
  }, [seconds])

  if (user) return <Navigate to={postAuthPath(user)} replace />

  const code = digits.join('')
  const complete = code.length === DIGITS

  const setDigitAt = (index: number, value: string) => {
    setDigits((current) => {
      const next = [...current]
      next[index] = value
      return next
    })
  }

  const onChange = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/\D/g, '').slice(-1)
    setDigitAt(index, value)
    if (value && index < DIGITS - 1) inputs.current[index + 1]?.focus()
  }

  const onKeyDown = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      inputs.current[index - 1]?.focus()
    }
  }

  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, DIGITS)
    if (!pasted) return
    event.preventDefault()
    setDigits(Array.from({ length: DIGITS }, (_, i) => pasted[i] ?? ''))
    inputs.current[Math.min(pasted.length, DIGITS - 1)]?.focus()
  }

  const verify = async () => {
    setBusy(true)
    setError(null)
    try {
      const { token, user: me } = await api.verifyOtp(code)
      setSession(token, me)
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  const resend = () => {
    setSeconds(RESEND_SECONDS)
    setDigits(Array(DIGITS).fill(''))
    inputs.current[0]?.focus()
  }

  return (
    <AuthShell>
      {(locale) => {
        const t = COPY[locale]
        return (
          <>
            <span className="auth__wordmark">JoPath</span>
            <div className="auth__head">
              <h1>{t.title}</h1>
              <p>{t.subtitle(contact)}</p>
            </div>
            {error ? (
              <div className="alert alert--error" role="alert">
                {error}
              </div>
            ) : null}
            <fieldset className="stack">
              <legend className="field__label">{t.label}</legend>
              <div className="auth__otp" dir="ltr">
                <div className="auth__otp-group">
                  {digits.slice(0, 3).map((digit, index) => (
                    <input
                      key={index}
                      ref={(element) => {
                        inputs.current[index] = element
                      }}
                      className="auth__otp-input"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      disabled={busy}
                      onChange={onChange(index)}
                      onKeyDown={onKeyDown(index)}
                      onPaste={onPaste}
                      aria-label={`${t.label} ${index + 1}`}
                    />
                  ))}
                </div>
                <span className="auth__otp-sep" aria-hidden="true">
                  –
                </span>
                <div className="auth__otp-group">
                  {digits.slice(3).map((digit, offset) => {
                    const index = offset + 3
                    return (
                      <input
                        key={index}
                        ref={(element) => {
                          inputs.current[index] = element
                        }}
                        className="auth__otp-input"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        disabled={busy}
                        onChange={onChange(index)}
                        onKeyDown={onKeyDown(index)}
                        onPaste={onPaste}
                        aria-label={`${t.label} ${index + 1}`}
                      />
                    )
                  })}
                </div>
              </div>
              <p className="auth__resend">
                <Icon name="clock" size={14} />
                {t.noCode}{' '}
                {seconds > 0 ? (
                  <strong>{t.resendIn(seconds)}</strong>
                ) : (
                  <button type="button" className="link-button" onClick={resend}>
                    {t.resend}
                  </button>
                )}
              </p>
              <Button variant="primary" busy={busy} disabled={!complete} className="btn--block" onClick={verify}>
                {t.submit}
              </Button>
            </fieldset>
            <Link to="/login" className="back-link auth__back">
              <Icon name="chevronRight" size={16} />
              {t.back}
            </Link>
          </>
        )
      }}
    </AuthShell>
  )
}
